import { useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, type LucideIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export type CancellationNoticeTone = "success" | "warning";

export interface CancellationNotice {
  tone: CancellationNoticeTone;
  title: string;
  description: ReactNode;
  actionLabel: string;
}

// Mesmos tons da seção: verde (primary) no sucesso, âmbar no que não pôde seguir.
const TONES: Record<CancellationNoticeTone, { icon: LucideIcon; className: string }> = {
  success: { icon: CheckCircle2, className: "bg-primary/10 text-primary" },
  warning: {
    icon: AlertTriangle,
    className: "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400",
  },
};

interface CancellationNoticeDialogProps {
  notice: CancellationNotice | null;
  onClose: () => void;
}

/** Aviso da seção de cancelamento: confirmação da solicitação ou pedido que não pode mais ser cancelado. */
export const CancellationNoticeDialog = ({ notice, onClose }: CancellationNoticeDialogProps) => {
  // Guarda o último aviso pra o texto não sumir durante a animação de saída, quando `notice` já é null.
  const [shownNotice, setShownNotice] = useState(notice);
  if (notice && notice !== shownNotice) setShownNotice(notice);

  const { icon: Icon, className: toneClassName } = TONES[shownNotice?.tone ?? "success"];

  return (
    // O botão (AlertDialogAction) e o Esc passam pelo mesmo `onOpenChange(false)`.
    <AlertDialog open={!!notice} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent className="max-w-[calc(100%-2rem)] gap-6 rounded-lg p-8 sm:max-w-md">
        <AlertDialogHeader className="items-center space-y-3 text-center sm:text-center">
          <span
            aria-hidden="true"
            className={cn("mb-1 flex h-14 w-14 items-center justify-center rounded-full", toneClassName)}
          >
            <Icon className="h-7 w-7" />
          </span>
          <AlertDialogTitle className="text-xl font-bold leading-tight">{shownNotice?.title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[15px] leading-relaxed">
            {shownNotice?.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction className="w-full">{shownNotice?.actionLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
